import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  TableContainer,
  Link,
} from "@mui/material";
import { Theme } from "@mui/material";

export const SPACING = {
  small: "8px",
  medium: "12px",
  large: "20px",
  xlarge: "30px",
} as const;

export const SIZES = {
  buttonHeight: "34px",
  borderRadius: "4px",
  fontSize: "36px",
  iconSize: "30px",
} as const;

// Button styles
export const buttonStyles = (theme: Theme) => ({
  height: SIZES.buttonHeight,
  borderRadius: SIZES.borderRadius,
  backgroundColor: theme.palette.neutral.white,
  color: theme.palette.neutral.black,
  border: `1px solid ${theme.palette.neutral.grey}`,
  margin: `${SPACING.small} 0`,
});

export const buttonStylesDisabled = (theme: Theme) => ({
  ...buttonStyles(theme),
  pointerEvents: "none" as const,
  cursor: "not-allowed" as const,
  opacity: 0.3,
});

export const inputStyles = (theme: Theme) => ({
  "& .MuiInputBase-root": {
    height: "23px !important",
  },
  "& .MuiInputBase-root.Mui-focused fieldset": {
    borderColor: theme.palette?.focusColor?.main,
  },
});

export const inputFileStyles = (theme: Theme) => ({
  "& .MuiInputBase-root.Mui-focused fieldset": {
    borderColor: theme.palette?.focusColor?.main,
  },
});

// Styled components
export const Container = styled(Box)(({ theme }) => ({
  paddingRight: 0,
  paddingLeft: 0,
  backgroundColor: theme.palette.neutral.whiteSmoke,
  width: "100%",
  height: "calc(100vh - 64px)", // Match MainSequence height (64px is headerHeight)
}));

export const StepperContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral.whiteSmoke,
  margin: "0 200px",
  minHeight: "40px",
}));

export const ExistingLinksBox = styled(Box)({
  textAlign: "center",
  paddingTop: "10px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: SPACING.small,
});

export const NavigationContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral.whiteSmoke,
  padding: `${SPACING.small} 0`,
}));

export const StepContent = styled(Box)(({ theme }) => ({
  padding: SPACING.large,
  boxShadow: "0 1px 1px 0 #3c404314, 0 1px 3px 1px #3c404329",
  backgroundColor: theme.palette.neutral.white,
  borderRadius: 4,
  margin: `${SPACING.large} 100px`,
  maxHeight: 570,
}));

export const StyledTextField = styled(TextField)({
  margin: 8,
  minWidth: 200,
  "& .MuiInputBase-root": {
    height: `${SIZES.buttonHeight} !important`,
  },
});

export const Title = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  marginBottom: 5,
  color: theme.palette.neutral.black,
  fontWeight: 700,
  fontSize: SIZES.fontSize,
  fontFamily: "Helvetica",
}));

export const CheckboxGroup = styled(Box)({
  margin: `${SPACING.small} 0px`,
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: "12px",
});

export const NavigationButtons = styled(Box)({
  textAlign: "center",
  marginTop: SPACING.medium,
  display: "flex",
  justifyContent: "center",
  gap: "16px",
});

export const ProgressContainer = styled(Box)({
  textAlign: "center",
  marginTop: 10,
  fontSize: 25,
});

export const TableContainerStyled = styled(TableContainer)({
  marginTop: 8,
  height: "100%",
  overflow: "hidden",
});

export const DownloadLink = styled(Link)(({ theme }) => ({
  color: theme.palette.neutral.linkBlue,
  textDecoration: "none",
  fontWeight: 600,
  "&:hover": {
    textDecoration: "underline",
    color: theme.palette.neutral.darkerLinkBlue,
  },
}));

export const FormRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  marginRight: -5,
  marginLeft: -5,
});

export const CheckboxInline = styled(FormControlLabel)({
  cursor: "default",
  marginLeft: 0,
});

export const StyledBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  backgroundColor: "transparent",
});

export const StepItem = styled(Box)<{
  isActive?: boolean;
  isCompleted?: boolean;
}>(({ isActive, isCompleted, theme }) => ({
  backgroundColor: isActive
    ? theme.palette.neutral.seaSerpent
    : isCompleted
      ? theme.palette.neutral.green
      : theme.palette.neutral.lightGrayish,
  border: isActive
    ? `1px solid ${theme.palette.neutral.darkBlue}`
    : isCompleted
      ? `1px solid ${theme.palette.neutral.green}`
      : `1px solid ${theme.palette.neutral.lightGrayish}`,
  boxShadow: isActive
    ? `1px 2px ${theme.palette.neutral.darkBlue}`
    : isCompleted
      ? `1px 2px ${theme.palette.neutral.darkGreen}`
      : `1px 2px ${theme.palette.neutral.shadowGrey}`,
  padding: theme.spacing(0.5, 1),
  color: theme.palette.neutral.white,
  fontWeight: "700",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  minHeight: "40px",
  "&:hover": {
    opacity: 0.9,
  },
}));

// Inline style variables
export const inlineStyles = {
  fileInput: {
    display: "none",
  },
  icon: {
    width: `${SIZES.iconSize}`,
    height: `${SIZES.iconSize}`,
  },
  disabledButton: {
    pointerEvents: "none" as const,
    cursor: "default" as const,
  },
  uploadButton: {
    marginTop: 2,
  },
  linkedHeader: {
    textAlign: "center" as const,
    marginTop: 2,
  },
  formRow: {
    padding: 1.25,
  },
  progressTypography: {
    marginTop: 2,
  },
  searchContainer: {
    display: "flex",
    justifyContent: "end",
  },
  dataGridContainer: {
    maxHeight: "180px",
    width: "100%",
  },
  linkedCategoriesDataGridContainer: {
    maxHeight: "250px",
    width: "100%",
  },
  searchStyles: {
    margin: "10px",
  },
  selectStoresCatalogsContainer: {
    margin: 5,
  },
  descriptionContainer: {
    "&.MuiBox-root": {
      padding: "5px !important",
    },
  },
  dataGridStyles: (theme: Theme) => ({
    backgroundColor: theme.palette.neutral.white,
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.neutral.gray}`,
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: theme.palette.neutral.whiteSmoke,
      borderBottom: `2px solid ${theme.palette.neutral.gray}`,
      position: "sticky",
      top: 0,
      zIndex: 1,
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "590",
      },
    },
    "& .MuiDataGrid-footerContainer": {
      position: "sticky",
      bottom: 0,
      backgroundColor: theme.palette.neutral.whiteSmoke,
      zIndex: 1,
    },
    "& .header-row": {
      fontWeight: "590",
      "& .MuiDataGrid-cell": {
        borderBottom: `2px solid ${theme.palette.neutral.gray}`,
      },
    },
  }),
  tableBox: {
    height: "220px",
  },
  linkedCategoriesTableBox: {
    height: "250px",
  },
} as const;
